import { clientEnv, type CryptoMode } from '@/shared/config/env';

export interface CryptoRequestInput {
  bodyText: string;
  method: string;
  path: string;
  query: string;
}

export interface CryptoRequestOutput {
  bodyText: string;
  headers: Record<string, string>;
}

const encoder = new TextEncoder();

// 必须是小写 hex：服务端 CryptoEntryFilter 用 HexFormat.of() 产出签名与 bodyHash，
// 且比对是字节精确的（MessageDigest.isEqual），换成 base64url 会一律 401 CRYPTO_INVALID。
const toHex = (bytes: ArrayBuffer) =>
  [...new Uint8Array(bytes)].map((byte) => byte.toString(16).padStart(2, '0')).join('');

const digestSha256 = async (text: string) => {
  const hash = await crypto.subtle.digest('SHA-256', encoder.encode(text));
  return toHex(hash);
};

const importHmacKey = async (secret: string) => {
  const keyBytes = await crypto.subtle.digest('SHA-256', encoder.encode(`${secret}:hmac`));
  return crypto.subtle.importKey('raw', keyBytes, { hash: 'SHA-256', name: 'HMAC' }, false, ['sign']);
};

const signHmac = async (secret: string, canonicalText: string) => {
  const key = await importHmacKey(secret);
  const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(canonicalText));
  return toHex(signature);
};

const buildCanonicalText = (input: {
  bodyHash: string;
  keyId: string;
  method: string;
  nonce: string;
  path: string;
  query: string;
  timestamp: string;
  version: string;
}) =>
  [
    input.version,
    input.timestamp,
    input.nonce,
    input.method.toUpperCase(),
    input.path,
    input.query,
    input.keyId,
    input.bodyHash,
  ].join('\n');

const shouldSkipCrypto = (mode: CryptoMode) => mode === 'none' || !clientEnv.cryptoTestSecret;

export const protectRequest = async (
  request: CryptoRequestInput,
): Promise<CryptoRequestOutput> => {
  const mode = clientEnv.cryptoMode;

  if (shouldSkipCrypto(mode)) {
    return {
      bodyText: request.bodyText,
      headers: {},
    };
  }

  // 浏览器端静态密钥只能用于本地或测试联调，生产应改为服务端下发的短期会话密钥或 APP 安全存储。
  const version = 'v1';
  const keyId = clientEnv.cryptoKeyId;
  const timestamp = Date.now().toString();
  const nonce = crypto.randomUUID();
  const bodyHash = await digestSha256(request.bodyText);
  const canonicalText = buildCanonicalText({
    bodyHash,
    keyId,
    method: request.method,
    nonce,
    path: request.path,
    query: request.query,
    timestamp,
    version,
  });
  const signature = await signHmac(clientEnv.cryptoTestSecret, canonicalText);

  return {
    bodyText: request.bodyText,
    headers: {
      'X-Body-Hash': bodyHash,
      'X-Crypto-Version': version,
      'X-Key-Id': keyId,
      'X-Nonce': nonce,
      'X-Signature': signature,
      'X-Timestamp': timestamp,
    },
  };
};
